import { Typography } from '@mui/material';
import { theme } from '@theguarantors/tg-package-material-ui';

import { useSplitFeatureFlag } from '@/hooks/useSplitFeatureFlag';
import { SplitFeatureFlag } from '@/utils/split/splitConfig';
import { Renter } from '@/utils/types/renter.interface';

import { InviteLeaseDetailsDrawerForm } from './invite-lease-details.drawer.form';
import { InviteLeaseDetailsSharedForm } from './invite-lease-details.shared-form';
import { useInviteLeaseDetailsController } from './hooks';
import type { UseInviteLeaseDetailsControllerResult } from './hooks';
import type { InviteFlowMilestoneKey } from './types';

export type InviteLeaseDetailsDrawerFormContentProps = {
  onSuccess?: (renter: Renter) => void;
  onMilestoneReached?: (key: InviteFlowMilestoneKey) => void;
};

type LegacyInviteLeaseDetailsFormProps = {
  controller: UseInviteLeaseDetailsControllerResult;
  onMilestoneReached?: (key: InviteFlowMilestoneKey) => void;
};

// Legacy drawer form (kill-switch OFF / fallback during ramp).
const LegacyInviteLeaseDetailsForm = ({
  controller,
  onMilestoneReached,
}: LegacyInviteLeaseDetailsFormProps) => {
  const { user, errorMsg, isSmbInvite, useNewForm, customCoverageDisabled, onSubmit } = controller;

  return (
    <InviteLeaseDetailsDrawerForm
      user={user}
      onSubmit={onSubmit}
      errorMsg={errorMsg}
      newCoveragesEnabled={useNewForm}
      customCoverageDisabled={customCoverageDisabled}
      isSmbInvite={isSmbInvite}
      onMilestoneReached={onMilestoneReached}
    />
  );
};

export const InviteLeaseDetailsDrawerFormContent = ({
  onSuccess,
  onMilestoneReached,
}: InviteLeaseDetailsDrawerFormContentProps) => {
  const { isReady, isFeatureFlagEnabled } = useSplitFeatureFlag(
    SplitFeatureFlag.OTTERS_4671_INVITE_USE_SHARED_LEASE_DETAILS_FORM,
  );

  // One controller instance for both paths: a single on-load "Renter Invite form
  // Visited" event, and the legacy↔shared swap (when the flag resolves) never
  // remounts it.
  const controller = useInviteLeaseDetailsController({ onSuccess });

  return (
    <>
      <Typography
        mb={3}
        letterSpacing={0}
        sx={{
          fontSize: '16px !important',
          [theme.breakpoints.down('md')]: {
            fontSize: '14px !important',
            lineHeight: '22px',
          },
        }}
      >
        Please fill out your applicant's information below, and we will send them an invitation to
        apply using the email address provided.
      </Typography>
      {/* Legacy renders until the flag resolves (no blank flash, matches today's
          render timing); swaps to the shared form only when the kill-switch is on. */}
      {isReady && isFeatureFlagEnabled ? (
        <InviteLeaseDetailsSharedForm
          controller={controller}
          onMilestoneReached={onMilestoneReached}
        />
      ) : (
        <LegacyInviteLeaseDetailsForm
          controller={controller}
          onMilestoneReached={onMilestoneReached}
        />
      )}
    </>
  );
};
